/**
 * Pinia-стор для `QuizBrief` (/:lang/brief).
 *
 * Контракт:
 *   - `selections: Record<stepId, string | string[]>` — ответы (single/multi).
 *   - `extras: Record<stepId, string>` — текстовые доп-поля (aux, custom).
 *   - `currentStep: number`.
 *   - `draftSavedAt: number | null`.
 *   - Persistence: `localStorage['bafsy.brief.draft.v1']`.
 */

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { briefSchema, getBriefSchema } from '@/content/brief-schema';
import type { BriefSchema } from '@/content/types';
import type { Locale } from '@/i18n/locales';
import type { BriefDraftPayload as DraftPayload } from '@/types/store-types';

const DRAFT_KEY = 'bafsy.brief.draft.v1';
const DRAFT_VERSION = 1;

function safeReadDraft(): DraftPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const p = parsed as Partial<DraftPayload>;
    if (p.version !== DRAFT_VERSION) return null;
    if (typeof p.currentStep !== 'number' || p.currentStep < 0) return null;
    if (!p.selections || typeof p.selections !== 'object') return null;
    return {
      version: DRAFT_VERSION,
      selections: p.selections as Record<string, string | readonly string[]>,
      extras: (p.extras && typeof p.extras === 'object' ? p.extras : {}) as Record<string, string>,
      currentStep: p.currentStep,
      savedAt: typeof p.savedAt === 'number' ? p.savedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

function safeWriteDraft(payload: DraftPayload): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function safeClearDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}

export const useBriefStore = defineStore('brief', () => {
  const schema = ref<BriefSchema>(briefSchema);
  const selections = ref<Record<string, string | readonly string[]>>({});
  const extras = ref<Record<string, string>>({});
  const currentStep = ref<number>(0);
  const draftSavedAt = ref<number | null>(null);
  const submitted = ref<boolean>(false);

  const steps = computed(() => schema.value.steps);
  const stepCount = computed(() => steps.value.length);
  const currentStepId = computed(() => steps.value[currentStep.value]?.id ?? '');
  const isLastStep = computed(() => currentStep.value >= stepCount.value - 1);
  const isFirstStep = computed(() => currentStep.value === 0);
  const hasDraft = computed<boolean>(() => safeReadDraft() !== null);

  function saveDraft(): void {
    const ts = Date.now();
    draftSavedAt.value = ts;
    safeWriteDraft({
      version: DRAFT_VERSION,
      selections: selections.value,
      extras: extras.value,
      currentStep: currentStep.value,
      savedAt: ts,
    });
  }

  function set(stepId: string, value: string | readonly string[]): void {
    selections.value = { ...selections.value, [stepId]: value };
    saveDraft();
  }

  function setExtra(fieldId: string, value: string): void {
    extras.value = { ...extras.value, [fieldId]: value };
    saveDraft();
  }

  function next(): void {
    if (currentStep.value < stepCount.value - 1) {
      currentStep.value += 1;
      saveDraft();
    }
  }

  function back(): void {
    if (currentStep.value > 0) {
      currentStep.value -= 1;
      saveDraft();
    }
  }

  function goTo(index: number): void {
    if (index < 0 || index >= stepCount.value) return;
    currentStep.value = index;
    saveDraft();
  }

  function reset(): void {
    selections.value = {};
    extras.value = {};
    currentStep.value = 0;
    draftSavedAt.value = null;
    submitted.value = false;
    safeClearDraft();
  }

  function resumeDraft(): boolean {
    const draft = safeReadDraft();
    if (!draft) return false;
    selections.value = { ...draft.selections };
    extras.value = { ...draft.extras };
    currentStep.value = Math.min(draft.currentStep, stepCount.value - 1);
    draftSavedAt.value = draft.savedAt;
    return true;
  }

  function markSubmitted(): void {
    submitted.value = true;
  }

  function setLocale(locale: Locale): void {
    schema.value = getBriefSchema(locale);
  }

  return {
    schema,
    selections,
    extras,
    currentStep,
    draftSavedAt,
    submitted,
    steps,
    stepCount,
    currentStepId,
    isLastStep,
    isFirstStep,
    hasDraft,
    set,
    setExtra,
    next,
    back,
    goTo,
    reset,
    resumeDraft,
    saveDraft,
    markSubmitted,
    setLocale,
  };
});
